import { useMemo } from "react";

import { useDashboardLeaguesControllerOnboarding } from "@/api/__generated__/dashboard-leagues/dashboard-leagues";
import { useCurrentLeague } from "@/contexts";
import { useScopedHref } from "@/hooks";
import { mapToLeagueOnboardingStep } from "@/mappers";
import { LeagueOnboardingStepId, OnboardingStepView } from "@/types";

import {
  LEAGUE_ONBOARDING_STEP_ORDER,
  useLeagueOnboardingStepMeta,
} from "./leagueOnboardingStepMeta";

/**
 * Shared league-onboarding data layer for the three surfaces (dedicated page,
 * overview card, sidebar banner). Fetches the league's onboarding steps, drops
 * the ones whose module is disabled (`enableFactSheet` / `enableRankings` —
 * missing toggles default to enabled so nothing regresses), and builds the
 * scope-agnostic `OnboardingStepView[]` with each deep-link `href` already
 * resolved through `useScopedHref`. Also derives completion progress for the
 * banner. The read is the backend authz gate: a successful read means the
 * caller manages this league; `isError` (403/404/…) means they cannot.
 */
export const useLeagueOnboarding = (leagueId: string) => {
  const scopedHref = useScopedHref();
  const currentLeague = useCurrentLeague();
  const stepMeta = useLeagueOnboardingStepMeta();

  const enableRankings = currentLeague?.enableRankings ?? true;
  const enableFactSheet = currentLeague?.enableFactSheet ?? true;

  const query = useDashboardLeaguesControllerOnboarding(leagueId, {
    query: { enabled: !!leagueId },
  });
  const { data } = query;

  const completedStepIds = useMemo(() => {
    const steps = (data ?? []).map(mapToLeagueOnboardingStep);
    return new Set(steps.filter((step) => step.isCompleted).map((step) => step.step));
  }, [data]);

  const visibleStepIds = useMemo<LeagueOnboardingStepId[]>(
    () =>
      LEAGUE_ONBOARDING_STEP_ORDER.filter((id) => {
        if (!enableFactSheet && id === "fact-sheet") return false;
        if (!enableRankings && id === "ranking") return false;
        return true;
      }),
    [enableFactSheet, enableRankings]
  );

  const stepViews = useMemo<OnboardingStepView[]>(
    () =>
      visibleStepIds.map((id) => {
        const meta = stepMeta[id];
        return {
          id,
          title: meta.title,
          description: meta.description,
          icon: meta.icon,
          href: scopedHref(`/league/${leagueId}/${meta.path}`),
          isCompleted: completedStepIds.has(id),
        };
      }),
    [visibleStepIds, stepMeta, completedStepIds, scopedHref, leagueId]
  );

  const allComplete = stepViews.length > 0 && stepViews.every((step) => step.isCompleted);
  const percentage =
    stepViews.length === 0
      ? 0
      : Math.round((stepViews.filter((step) => step.isCompleted).length / stepViews.length) * 100);

  return {
    ...query,
    stepViews,
    allComplete,
    percentage,
  };
};
